/* useDealActivity.js — Lightweight per-deal activity fetcher.
 *
 * Reads from the real Postgres `deal_activities` table via
 * `/api/v2/deals/{id}/activity`. Caches per-deal in-memory so a
 * single Deal Room render hits the network once. Returns the rows
 * ordered most-recent-first (matches the server response order).
 *
 * Doctrine: real operational data > rotating placeholders.
 */
import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";

const _cache = new Map(); // dealId → array of activity rows

export function useDealActivity(dealId, { limit = 20, refreshKey = 0 } = {}) {
  const [rows, setRows] = useState(() => (dealId ? (_cache.get(dealId) || null) : null));
  const inFlight = useRef(null);

  useEffect(() => {
    if (!dealId) { setRows(null); return; }
    // refreshKey > 0 bypasses cache so callers can force a re-fetch
    // after a write (e.g., logging a new milestone activity).
    if (refreshKey === 0 && _cache.has(dealId)) { setRows(_cache.get(dealId)); return; }
    if (inFlight.current === dealId) return;
    inFlight.current = dealId;

    (async () => {
      try {
        const { data } = await api.get(`/api/v2/deals/${dealId}/activity?limit=${limit}`);
        const list = Array.isArray(data) ? data : [];
        _cache.set(dealId, list);
        if (inFlight.current === dealId) setRows(list);
      } catch {
        _cache.set(dealId, []);
        if (inFlight.current === dealId) setRows([]);
      } finally {
        inFlight.current = null;
      }
    })();
  }, [dealId, limit, refreshKey]);

  return rows; // null while loading; [] when empty; populated array otherwise
}

/** Invalidate the cached activity for a deal — call after a write. */
export function invalidateDealActivity(dealId) {
  _cache.delete(dealId);
}
